import { ComponentProps, useState } from "react";

import {
  Button,
  ButtonProps,
  Menu,
  MenuItem,
  MenuProps,
  Tooltip,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import _ from "lodash";
import { Redirect, useLocation } from "react-router-dom";

import { Black } from "assets/colors";

const useTooltipStyles = makeStyles({
  tooltip: {
    backgroundColor: "#fff",
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
    fontSize: "12px",
    padding: "8px 12px",
    borderRadius: "4px",
  },
  arrow: {
    color: "#fff",
  },
});

export interface IMenuButtonRedirectOption {
  label: string;
  redirect: true;
  icon?: React.ReactNode;
  key?: string;
  disabled?: boolean;
}

export interface IMenuButtonOnClickOption {
  label: React.ReactNode;
  onClick?: (...args: any) => void;
  icon?: React.ReactNode;
  key?: string;
  disabled?: boolean;
}

export type TMenuButtonOption =
  IMenuButtonRedirectOption | IMenuButtonOnClickOption;

export interface IMenuButton {
  className?: string;
  label: React.ReactNode;
  color?: string;
  fontColor?: string;
  icon?: JSX.Element;
  options: TMenuButtonOption[];
  disabled?: boolean;
  buttonProps?: ButtonProps;
  menuProps?: Partial<MenuProps>;
  menuItemProps?: Partial<ComponentProps<typeof MenuItem>>;
  alwaysDropDown?: boolean;
  fullWidth?: boolean;
  tooltipText?: string;
}

export const MenuButton: React.FunctionComponent<IMenuButton> = ({
  className,
  label,
  options,
  color,
  fontColor,
  icon,
  disabled = false,
  buttonProps,
  menuProps,
  menuItemProps,
  alwaysDropDown,
  fullWidth = false,
  tooltipText,
}: IMenuButton) => {
  const tooltipClasses = useTooltipStyles();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getOnClick = (option: TMenuButtonOption) => {
    if ("redirect" in option) {
      return () => setRedirectTo(`${location.pathname}/create`);
    }

    const onOptionClick = option.onClick;
    if (!onOptionClick) {
      return;
    }

    return (...args: any) => {
      handleClose();
      onOptionClick(args);
    };
  };

  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  if (options.length === 1 && !alwaysDropDown) {
    const option = options[0];
    const onClick = getOnClick(option);
    return (
      <Button
        className={className}
        aria-controls="create-menu"
        aria-haspopup="true"
        aria-expanded="false"
        onClick={onClick}
        color="secondary"
        variant="contained"
        disableElevation
        disabled={option.disabled || disabled || !onClick}
        style={{
          position: "relative",
          background: color,
          color: fontColor,
          ...(buttonProps?.style || {}),
        }}
        {...(_.omit(buttonProps, "style") || {})}
      >
        {option.icon}
        {label}
      </Button>
    );
  }

  const endIcon = anchorEl ? (
    <span className="material-icons-outlined">expand_less</span>
  ) : (
    <span className="material-icons-outlined">expand_more</span>
  );

  const buttonElement = (
    <span
      style={{
        width: fullWidth ? "100%" : "auto",
        display: "inline-block",
      }}
    >
      <Button
        className={className}
        aria-controls="create-menu"
        aria-haspopup="true"
        aria-expanded="true"
        onClick={handleClick}
        color="secondary"
        variant="contained"
        disableElevation
        style={{
          position: "relative",
          background: color,
          color: fontColor,
          ...(buttonProps?.style || {}),
        }}
        startIcon={icon}
        disabled={disabled}
        endIcon={disabled ? undefined : endIcon}
        {...(_.omit(buttonProps, ["style", "onClick"]) || {})}
      >
        {label}
      </Button>
    </span>
  );

  return (
    <div style={{ width: fullWidth ? "100%" : "auto" }}>
      {tooltipText ? (
        <Tooltip
          title={tooltipText}
          arrow
          placement="top"
          disableHoverListener={!!anchorEl}
          classes={tooltipClasses}
        >
          {buttonElement}
        </Tooltip>
      ) : (
        buttonElement
      )}

      <Menu
        id="create-user-menu"
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        // https://github.com/mui/material-ui/issues/10804#issuecomment-376266662

        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={handleClose}
        style={{ marginTop: "8px", ...menuProps?.style }}
        {...menuProps}
      >
        {_.map(options, (option, index) => {
          const onClick = getOnClick(option);
          return (
            <MenuItem
              style={{ margin: "5px", ...(menuItemProps?.style || {}) }}
              key={index}
              onClick={onClick}
              disabled={!onClick || option.disabled}
              {..._.omit(menuItemProps, "style")}
            >
              {option.icon}
              <Typography
                component="div"
                style={{ fontSize: 16, fontWeight: 500, color: Black }}
              >
                {option.label}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};

export const AddIcon = ({ style = {} }: any) => {
  return (
    <span
      className="material-icons-outlined"
      style={{ marginLeft: "-4px", marginRight: "4px", ...style }}
    >
      add
    </span>
  );
};

export const DrawAddIcon = () => (
  <span
    className="material-icons-outlined"
    style={{ marginLeft: "-4px", marginRight: "4px" }}
  >
    draw
  </span>
);

export const ImportIcon = () => (
  <span
    className="material-icons-outlined"
    style={{ marginLeft: "-4px", marginRight: "4px" }}
  >
    file_upload
  </span>
);
export const AssignIcon = () => (
  <span
    className="material-icons-outlined"
    style={{ marginLeft: "-4px", marginRight: "4px", fontSize: "20px" }}
  >
    person_add
  </span>
);

export const UnassignIcon = () => (
  <span
    className="material-icons-outlined"
    style={{ marginLeft: "-4px", marginRight: "4px", fontSize: "20px" }}
  >
    person_remove
  </span>
);

export const EditIcon = () => (
  <span
    className="material-icons-outlined"
    style={{ marginLeft: "-4px", marginRight: "4px", fontSize: "20px" }}
  >
    edit
  </span>
);
export default MenuButton;
